import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const HERO_IMG = "https://storage.readdy-site.link/project_files/bac881ec-b9cc-4710-87b9-9f51b6eb607d/7c36d60d-9fe4-4706-b736-7fc84d50d4b9_Exterior-2.jpg?v=5d9ca8f72882f7b6fc9d9b4838843ff9";

const PRINCIPALS = [
  {
    name: "Grant Havasy",
    title: "Developer & Co-Founder",
    bio: "Grant Havasy is the Developer and Co-Founder of Havinar Homes, where he oversees acquisitions, financing, project strategy, and overall business operations. With extensive experience across residential development and real estate investment, Grant has been involved in the acquisition, development, and management of numerous properties throughout Long Island. His background spans ground-up development, value-add repositioning, and complex project structuring, with a focus on disciplined underwriting and execution. Prior to launching Havinar Homes, Grant played a key role in building and operating multiple real estate platforms, contributing to the development of hundreds of residential units and establishing long-standing relationships with lenders, partners, and local stakeholders.",
    img: "https://storage.readdy-site.link/project_files/bac881ec-b9cc-4710-87b9-9f51b6eb607d/cb40a6cb-cff3-4ff4-8e67-ef8f4cf4cb9f_02.jpg?v=dca12dbf9ccf632855aab4917e7aa609",
  },
  {
    name: "Bradley Halinar",
    title: "Builder Partner - Co Founder",
    bio: "Bradley Halinar serves as Builder Partner at Havinar Homes, where he leads all construction operations, project execution, and on-site management. Brad brings over two decades of hands-on experience in residential development, having managed and delivered numerous projects across Long Island. Since his early work on the Stoneleigh Woods development in Riverhead, he has played a key role in overseeing the construction of a wide range of projects — from ground-up builds to full-scale renovations. Known for his attention to detail, cost control, and ability to coordinate trades efficiently, Brad ensures that each project is executed to a high standard while maintaining schedule and budget discipline.",
    img: "https://storage.readdy-site.link/project_files/bac881ec-b9cc-4710-87b9-9f51b6eb607d/36b2be41-23fb-4840-8052-49da0d68b200_Exterior-3.jpg?v=2fc20a2e66ba2c6dcc9384055466b0a1",
  },
];

const VALUES = [
  { icon: "ri-shield-check-line", title: "Integrity", desc: "We operate with complete transparency — in our pricing, our timelines, and our process." },
  { icon: "ri-focus-3-line", title: "Precision", desc: "Every detail matters. From foundation to finish, we hold ourselves to the highest standard." },
  { icon: "ri-trophy-line", title: "Excellence", desc: "We don&apos;t settle for good enough. Each property we deliver reflects our commitment to outstanding quality." },
  { icon: "ri-team-line", title: "Partnership", desc: "We treat every project as a collaboration — whether with clients, contractors, or community." },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function AboutHero() {
  return (
    <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
      <img src={HERO_IMG} alt="About us hero" className="absolute inset-0 w-full h-full object-cover object-top" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
        <p className="text-amber-400 text-xs font-semibold tracking-[0.35em] uppercase mb-4 opacity-0 animate-[fadeInUp_0.8s_0.3s_forwards]">
          Our Story
        </p>
        <h1 className="text-white font-serif text-6xl md:text-7xl font-bold mb-6 opacity-0 animate-[fadeInUp_0.8s_0.5s_forwards]">
          About Us
        </h1>
        <p className="text-white/80 text-lg max-w-xl leading-relaxed opacity-0 animate-[fadeInUp_0.8s_0.7s_forwards]">
          Two principals. One shared vision. A relentless commitment to transforming properties into homes people love.
        </p>
      </div>
    </section>
  );
}

function StorySection() {
  const { ref, visible } = useInView();
  return (
    <section ref={ref} className="bg-white py-28 px-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
        <div className={`transition-all duration-1000 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
          <p className="text-amber-700 text-xs font-semibold tracking-[0.3em] uppercase mb-5">Who We Are</p>
          <h2 className="text-4xl md:text-5xl font-bold font-serif text-stone-900 leading-tight mb-8">
            Long Island&apos;s Premier<br /><em className="font-light">Residential Developer</em>
          </h2>
          <p className="text-stone-500 text-base leading-relaxed mb-6">
            Havinar Homes is a Long Island&ndash;based residential development firm focused on acquiring, renovating, and building high-quality homes in well-located communities. Founded by Grant Havasy and Bradley Halinar, the company combines disciplined real estate investment strategy with hands-on construction expertise.
          </p>
          <p className="text-stone-500 text-base leading-relaxed">
            Together, they bring a complementary partnership that integrates acquisitions, financing, and project execution into a streamlined development platform — built on a straightforward approach: identify strong locations, execute efficiently, and deliver well-designed, high-quality homes.
          </p>
        </div>
        <div className={`relative transition-all duration-1000 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
          <div className="rounded-2xl overflow-hidden h-[480px]">
            <img
              src="https://storage.readdy-site.link/project_files/bac881ec-b9cc-4710-87b9-9f51b6eb607d/b9169715-2e5c-4ebd-86c4-61998d295687_Exterior-12.jpg?v=ef56f73f6436f5ae2ad7fcd544ffe825"
              alt="Our work"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-amber-600 text-white p-8 rounded-2xl">
            <div className="text-4xl font-bold font-serif">40+</div>
            <div className="text-sm mt-1 text-amber-100">Years of Combined<br />Experience</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PrincipalsSection() {
  const { ref, visible } = useInView();
  return (
    <section ref={ref} className="bg-stone-50 py-28 px-8">
      <div className="max-w-7xl mx-auto">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-amber-700 text-xs font-semibold tracking-[0.3em] uppercase mb-4">The Team</p>
          <h2 className="text-4xl md:text-5xl font-bold font-serif text-stone-900">Meet the Principals</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {PRINCIPALS.map((p, i) => (
            <div
              key={p.name}
              className={`bg-white rounded-2xl overflow-hidden border border-stone-100 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${i * 200 + 100}ms` }}
            >
              <div className="p-10">
                <h3 className="text-4xl font-bold font-serif text-stone-900 mb-2">{p.name}</h3>
                <p className="text-amber-700 text-sm font-medium tracking-wide mb-5">{p.title}</p>
                <p className="text-stone-500 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: p.bio }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  const { ref, visible } = useInView();
  return (
    <section ref={ref} className="bg-white py-28 px-8">
      <div className="max-w-7xl mx-auto">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-amber-700 text-xs font-semibold tracking-[0.3em] uppercase mb-4">What Drives Us</p>
          <h2 className="text-4xl md:text-5xl font-bold font-serif text-stone-900">Our Core Values</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {VALUES.map((v, i) => (
            <div
              key={v.title}
              className={`text-center p-8 rounded-2xl bg-stone-50 hover:bg-amber-50 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 120 + 200}ms` }}
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-100 mx-auto mb-5">
                <i className={`${v.icon} text-2xl text-amber-700`} />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-3">{v.title}</h3>
              <p className="text-stone-500 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: v.desc }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutCTA() {
  const { ref, visible } = useInView();
  return (
    <section ref={ref} className="bg-stone-900 py-24 px-8 text-center">
      <div className={`max-w-2xl mx-auto transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <p className="text-amber-400 text-xs font-semibold tracking-[0.3em] uppercase mb-5">Our Work Speaks for Itself</p>
        <h2 className="text-4xl md:text-5xl font-bold font-serif text-white mb-6">
          Interested in Our Homes?
        </h2>
        <p className="text-stone-400 text-lg mb-10 leading-relaxed">
          If you&apos;d like to learn more about our current or upcoming properties, we&apos;d be happy to hear from you.
        </p>
        <Link
          to="/contact"
          className="whitespace-nowrap inline-block px-10 py-4 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold tracking-widest uppercase rounded-full transition-all duration-300 cursor-pointer"
        >
          Get in Touch &rarr;
        </Link>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <main>
      <Navbar />
      <AboutHero />
      <StorySection />
      <PrincipalsSection />
      <ValuesSection />
      <AboutCTA />
      <Footer />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Inter:wght@300;400;500;600&display=swap');
        body { font-family: 'Inter', sans-serif; }
        .font-serif { font-family: 'Playfair Display', Georgia, serif; }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </main>
  );
}
